import React from 'react';

import { Typography } from 'components/shared/Typography';

import { TextTextProperty } from '../TextTextProperty';
import { EmptyProperties } from '../EmptyProperties';

import { ReactionTransitionDirectionField } from './ReactionPropertyField';

import {
  getInteractionPropertyConfigs,
  getReactionCheckPrefix,
  getReactionsPropertyPrefix,
  type ReactionPropertyConfig,
} from './PrototypeProperties.helpers';

import styles from './PrototypeProperties.module.scss';

const PrototypeProperties = ({
  isInitialState,
  readOnly,
  designType,
  namePrefix,
  properties,
  onPropertyChanged,
}: {
  isInitialState: boolean;
  readOnly: boolean;
  designType: string;
  namePrefix: string;
  properties: Array<{ key: string; value?: unknown; check?: boolean }>;
  onPropertyChanged?: (name: string, checked: boolean) => void;
}) => {
  const interactions = properties.find((property) => property.key === 'reactions');
  const interactionValues = Array.isArray(interactions?.value)
    ? (interactions.value as Array<Record<string, any>>)
    : [];
  const reactionsPropertyPrefix = getReactionsPropertyPrefix({
    namePrefix,
    properties,
  });
  const checkable = !isInitialState;

  if (!interactions || interactionValues.length === 0) {
    return (
      <div className={styles.properties}>
        <EmptyProperties />
      </div>
    );
  }

  const renderProperty = ({
    config,
    interactionIndex,
  }: {
    config: ReactionPropertyConfig;
    interactionIndex: number;
  }) => {
    const property = {
      key: config.key,
      value: config.value,
    };
    const checkPrefix =
      reactionsPropertyPrefix &&
      getReactionCheckPrefix({
        reactionsPropertyPrefix,
        interactionIndex,
        propertyKey: config.key,
      });

    if (config.useTransitionDirection) {
      return (
        <ReactionTransitionDirectionField
          key={config.key}
          checkable={checkable}
          readOnly={readOnly}
          namePrefix={checkPrefix ?? undefined}
          property={property}
          onChanged={onPropertyChanged}
        />
      );
    }

    const PropertyComponent = config.Component ?? TextTextProperty;

    return (
      <PropertyComponent
        key={config.key}
        checkable={checkable}
        isInitialState={isInitialState}
        readOnly={readOnly}
        designType={designType}
        namePrefix={checkPrefix ?? undefined}
        property={property}
        dependProperty={config.dependProperty ?? null}
        onChanged={onPropertyChanged}
      />
    );
  };

  return (
    <div className={styles.properties}>
      {interactionValues.map(
        (interaction: Record<string, any>, interactionIndex: number) => {
          const propertyConfigs = getInteractionPropertyConfigs(interaction);
          const triggerRowConfigs = propertyConfigs.filter((config) =>
            config.key.startsWith('interactionTrigger'),
          );
          const actionConfigs = propertyConfigs.filter(
            (config) => !config.key.startsWith('interactionTrigger'),
          );
          const transitionRowConfigs = actionConfigs.filter((config) =>
            ['interactionTransition', 'interactionTransitionDirection'].includes(
              config.key,
            ),
          );
          const easingRowConfigs = actionConfigs.filter((config) =>
            [
              'interactionTransitionEasing',
              'interactionTransitionDuration',
            ].includes(config.key),
          );
          const standaloneActionConfigs = actionConfigs.filter(
            (config) =>
              ![
                'interactionTransition',
                'interactionTransitionDirection',
                'interactionTransitionEasing',
                'interactionTransitionDuration',
                'interactionTransitionMatchLayers',
              ].includes(config.key),
          );
          const matchLayersConfig = actionConfigs.find(
            (config) => config.key === 'interactionTransitionMatchLayers',
          );

          return (
            <div className={styles.interaction} key={interactionIndex}>
              <Typography
                className={styles.interactionTitle}
                variant="body2"
                weight="medium"
                mode="compact"
              >
                Interaction {interactionIndex + 1}
              </Typography>
              <div className={styles.interactionInner}>
                {triggerRowConfigs.length > 0 && (
                  <div className={styles.interactionRow}>
                    {triggerRowConfigs.map((config) =>
                      renderProperty({ config, interactionIndex }),
                    )}
                  </div>
                )}

                {standaloneActionConfigs
                  .filter((config) => config.key === 'interactionNavigationType')
                  .map((config) => renderProperty({ config, interactionIndex }))}

                {standaloneActionConfigs
                  .filter(
                    (config) => config.key === 'interactionNavigationLinkUrl',
                  )
                  .map((config) => renderProperty({ config, interactionIndex }))}

                {transitionRowConfigs.length > 0 && (
                  <div className={styles.interactionRow}>
                    {transitionRowConfigs.map((config) =>
                      renderProperty({ config, interactionIndex }),
                    )}
                  </div>
                )}

                {matchLayersConfig &&
                  renderProperty({
                    config: matchLayersConfig,
                    interactionIndex,
                  })}

                {easingRowConfigs.length > 0 && (
                  <div className={styles.interactionRow}>
                    {easingRowConfigs.map((config) =>
                      renderProperty({ config, interactionIndex }),
                    )}
                  </div>
                )}

                {standaloneActionConfigs
                  .filter((config) =>
                    config.key.startsWith('interactionTransitionX'),
                  )
                  .map((config) => renderProperty({ config, interactionIndex }))}

                {standaloneActionConfigs
                  .filter((config) =>
                    config.key.startsWith('interactionTransitionY'),
                  )
                  .map((config) => renderProperty({ config, interactionIndex }))}

                {standaloneActionConfigs
                  .filter((config) =>
                    [
                      'interactionTransitionStiffness',
                      'interactionTransitionDamping',
                      'interactionTransitionMass',
                    ].includes(config.key),
                  )
                  .map((config) => renderProperty({ config, interactionIndex }))}

                {standaloneActionConfigs
                  .filter(
                    (config) =>
                      config.key === 'interactionPreserveScrollPosition',
                  )
                  .map((config) => renderProperty({ config, interactionIndex }))}
              </div>
            </div>
          );
        },
      )}
    </div>
  );
};

export default React.memo(PrototypeProperties) as any;
